import { cn } from '@ai-tutor/ui';

interface BreatheCircleProps {
  /** Size the breath eases to, and how long it takes to get there. */
  scale: number;
  stepSeconds: number;
  /** 0–1 through the whole session; the outer ring fills with it. */
  sessionProgress: number;
  /** Whole seconds left in this breath, shown in the middle while running. */
  countdown?: number;
  /**
   * At rest: smaller, so on a phone the choices and Start fit the first
   * screen, and breathing gently on its own as a preview of the pace.
   */
  resting?: boolean;
  /** After a session: smaller and still. */
  settled?: boolean;
  reducedMotion: boolean;
}

const RING_RADIUS = 48;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** The breath itself: eased per step while running, a slow loop at rest. */
function breathStyle(
  props: Pick<
    BreatheCircleProps,
    'scale' | 'stepSeconds' | 'resting' | 'reducedMotion'
  >
) {
  if (props.reducedMotion) return { transform: 'scale(0.7)' };
  // At rest the CSS `animate-breathe` loop owns the transform.
  if (props.resting) return undefined;
  return {
    transform: `scale(${props.scale})`,
    transitionProperty: 'transform',
    transitionDuration: `${props.stepSeconds}s`,
    transitionTimingFunction: 'cubic-bezier(0.37, 0, 0.63, 1)',
  };
}

/**
 * The stage: a breathing circle in the brand ramp inside a ring that fills
 * over the session. Running, the step's own length IS the transition, so the
 * circle arrives at its size exactly when the cue changes. Still when motion
 * is reduced - the caption's progress bar carries the timing instead.
 */
export function BreatheCircle(props: BreatheCircleProps) {
  const {
    sessionProgress,
    countdown,
    resting = false,
    settled = false,
  } = props;
  const small = resting || settled;
  const done = Math.min(1, Math.max(0, sessionProgress));
  return (
    <div
      aria-hidden='true'
      className={cn(
        'relative flex items-center justify-center transition-[width,height] duration-slow',
        small ? 'h-40 w-40 sm:h-64 sm:w-64' : 'h-64 w-64 sm:h-80 sm:w-80'
      )}
    >
      <svg viewBox='0 0 100 100' className='absolute inset-0 -rotate-90'>
        <circle
          cx='50'
          cy='50'
          r={RING_RADIUS}
          fill='none'
          strokeWidth='1'
          className='stroke-brand-200 dark:stroke-brand-500/25'
        />
        <circle
          cx='50'
          cy='50'
          r={RING_RADIUS}
          fill='none'
          strokeWidth='1.25'
          strokeLinecap='round'
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - done)}
          className='stroke-brand-500 transition-[stroke-dashoffset] duration-200 ease-linear dark:stroke-brand-300'
        />
      </svg>
      <span className='absolute inset-[8%] rounded-full bg-brand-100/50 dark:bg-brand-500/10' />
      <span
        className={cn(
          'h-[82%] w-[82%] rounded-full bg-gradient-to-br from-brand-200 to-brand-400 shadow-elevation-2',
          'dark:from-brand-400/60 dark:to-brand-600/60',
          resting && !props.reducedMotion && 'animate-breathe',
          settled && 'scale-[0.55]'
        )}
        style={settled ? undefined : breathStyle(props)}
      />
      {countdown !== undefined && (
        <span className='absolute font-mono text-4xl font-semibold tabular-nums text-brand-950 dark:text-foreground'>
          {countdown}
        </span>
      )}
    </div>
  );
}
